<script setup>
import { computed } from 'vue';

const props = defineProps({
  data: { type: Array, required: true }
});

const getCellStyle = (rate) => {
  if (rate === null || rate === undefined) return { 
    backgroundColor: 'var(--result-ai-primary-65)',
    width: '0%'
  };
  let backgroundColor = '';

  if (rate >= 95) backgroundColor = '#051B7C';
  else if (rate >= 90) backgroundColor = '#0357A3';
  else if (rate >= 85) backgroundColor = '#02A8D7';
  else if (rate >= 80) backgroundColor = '#1181F0';
  else if (rate >= 75) backgroundColor = '#01BDE4';
  else if (rate >= 70) backgroundColor = '#6AADDC';
  else if (rate >= 65) backgroundColor = '#A6A4D8';
  else if (rate >= 60) backgroundColor = '#9BA5D9';
  else backgroundColor = '#F895D1';
  
  return { 
    backgroundColor,
    width: `${rate}%`
  };
};

// 정답률 범위 생성 (0-100%, 10% 간격)
const rateRanges = computed(() => Array.from({ length: 11 }, (_, i) => i * 10));
</script>


<template>
  <div class="card heatmap">
    <h5 class="card-title">영역별 정답률(%)</h5>
    <p class="msg-info py-2">다음은 AI·디지털 리터러시 진단검사의 영역별 정답률을 분석한 결과입니다.</p>
    <div class="heatmap-wrapper" v-if="data && data.length > 0">
      
      <n-flex class="chart-wrap" :wrap="false" :size="0" align='flex-end'>
      
        <div class="y-axis-labels">         
          <div 
            v-for="row in data" 
            :key="row.category" 
            class="y-label"
          >
            {{ row.category }}
          </div>         
        </div>

        <div class="heatmap-grid-container">        
          <div class="x-axis-scale">
            <div class="scale-line" v-for="i in 11" :key="i" :style="{ left: `${(i-1) * 10}%` }">
              <span>{{ (i-1) * 10 }}%</span>
            </div>
          </div>
          <div class="heatmap-grid">
            <div 
              v-for="(row, rIdx) in data" 
              :key="rIdx"
              class="bar-container"
            >
              <div 
                class="grid-cell"
                :style="getCellStyle(row.rate)"
              >
                <span class="rate-text">{{ row.rate.toFixed(1) }}%</span>
              </div>
            </div>
          </div>         
        </div>
        
        <!-- <div class="heatmap-legend">
          <div class="legend-bar"></div>
          <div class="legend-labels">
            <span>95</span><span>90</span><span>85</span><span>80</span>
            <span>75</span><span>70</span><span>65</span><span>60</span>
          </div>         
        </div> -->
      </n-flex>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.heatmap-wrapper {
  display: flex;
  flex-direction: column;
  padding: 1rem 2rem;
  position: relative;
  border: 1px solid var(--result-ai-border);
  border-radius: 8px;
  h5 {
    text-align: center;
    font-family: var(--smoe-font-visual);
    font-weight: 600;
    padding: .5rem 0;
    border-top: 2px solid var(--result-ai-primary-100);
    border-bottom: 3px solid var(--result-ai-primary-100);
  }
  .chart-wrap { 
    padding: 2.5rem 2.5rem 0 0;
    position: relative;
    &::before {
      content: '정답률 (%)';
      position: absolute;
      top: 1.5rem;
      left: 50%;
      transform: translateX(-50%);
      font-size: 1rem;
      color: var(--result-ai-primary-800);
      font-weight: 500;
    }
  }
}
.y-axis-labels {
  display: flex;
  flex-direction: column;
  gap: 1px; 
  width: 120px;
  flex-shrink: 0;
  position: relative;
  padding-top: 1px; 
  .y-label {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 12px;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
    text-align: right;
    word-break: keep-all;
    box-sizing: border-box;
    height: 60px;
    border-bottom: 1px solid var(--result-ai-primary-80);
    &:last-child { border: 0; }
  }  
   &::before {
    content: '영역';
    position: absolute;
    top: -20px;
    left: 0;
    display: inline-block;      
    font-size: 1rem;
    width: max-content;
    color: var(--result-ai-primary-800);
    font-weight: 500;
  }
}
.heatmap-grid-container {
  flex: 1;
  position: relative;
}
.x-axis-scale {
  position: relative;
  height: 25px;
  margin-bottom: 8px;
  .scale-line {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    &::before {
      content: '';
      width: 1px;
      height: 8px;
      background-color: var(--result-ai-primary-80);
      margin-bottom: 4px;
    }
    span {
      font-size: 12px;
      color: var(--result-ai-primary-800);
    }
  }
}
.heatmap-grid {
  position: relative;
  .bar-container {
    height: 60px;
    margin-bottom: 1px;
    position: relative;
    background-color: white;
    display: flex;
    align-items: center;
    &:last-child { margin-bottom: 0; }
  }
  .grid-cell {
    height: 60%;
    min-width: 60px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 16px;
    box-sizing: border-box;
    color: var(--smoe-white);
    font-weight: 600;
    border-radius: 0 4px 4px 0;
    position: relative;
    
    .rate-text {
      padding-right: 8px;
      white-space: nowrap;
    }
  }
}
.heatmap-legend {
  display: flex;
  gap: .25rem;
  position: relative;
  padding-left: 6px;
  height: 245px;
  .legend-bar {
    width: 12px;
    height: 100%;
    background: linear-gradient(180deg, #051B7C 0%, #00BEE5 47.6%, #FF95D1 100%);
    border-radius: 2px;
    position: relative;
    &::before {
      content: '정답률(%)';
      position: absolute;
      top: -24px;
      left: 0;
      display: inline-block;      
      font-size: 1rem;
      width: max-content;
      color: var(--result-ai-primary-800);
      font-weight: 500;  
    }
  }
  .legend-labels {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    font-size: 1rem;
    color: var(--result-ai-primary-800);
    span {
      position: relative;     
    }
  } 
}
</style>